import { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Plus, Trash2, Key, Copy, Check } from "lucide-react";

export default function Users() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [err, setErr] = useState("");
  const [resetLink, setResetLink] = useState(null); // { email, url, expires_at }
  const [copied, setCopied] = useState(false);

  const load = () => api.get("/api/admin/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    setErr("");
    try {
      await api.post("/api/admin/users", form);
      setCreating(false);
      setForm({ email: "", password: "", name: "" });
      await load();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  };
  const remove = async (u) => {
    if (!window.confirm(`Удалить ${u.email}?`)) return;
    try { await api.delete(`/api/admin/users/${u.id}`); await load(); }
    catch (e) { alert(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const issueReset = async (u) => {
    try {
      const { data } = await api.post(`/api/admin/users/${u.id}/reset-password`);
      setResetLink({ email: u.email, url: data.reset_url, expires_at: data.expires_at });
      setCopied(false);
    } catch (e) { alert(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const copyResetLink = async () => {
    try {
      await navigator.clipboard.writeText(resetLink.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch (e) { window.prompt("Скопируйте ссылку вручную:", resetLink.url); }
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <div className="label-mono">ПОЛЬЗОВАТЕЛИ</div>
          <h1 className="text-3xl md:text-4xl font-medium tracking-tight mt-2">{users.length}</h1>
        </div>
        <button className="btn-primary" onClick={() => setCreating(true)} data-testid="user-new-btn"><Plus size={14} /> Новый админ</button>
      </div>

      <div className="border border-black/10">
        <table className="w-full text-sm">
          <thead className="bg-secondary border-b border-black/10">
            <tr className="label-mono">
              <th className="text-left px-4 py-3">E-mail</th>
              <th className="text-left px-4 py-3">Имя</th>
              <th className="text-left px-4 py-3">Роль</th>
              <th className="text-left px-4 py-3">Создан</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-black/5" data-testid={`user-row-${u.id}`}>
                <td className="px-4 py-3">{u.email}</td>
                <td className="px-4 py-3">{u.name}</td>
                <td className="px-4 py-3"><span className="label-mono">{u.role}</span></td>
                <td className="px-4 py-3 mono text-xs text-foreground/60">{(u.created_at || "").slice(0, 10)}</td>
                <td className="px-4 py-3 text-right space-x-3 whitespace-nowrap">
                  {u.id !== user.id && (
                    <button className="hop-link text-xs" onClick={() => issueReset(u)} data-testid={`user-reset-${u.id}`}>
                      <Key size={12} className="inline" /> Сбросить пароль
                    </button>
                  )}
                  {u.id !== user.id && u.role !== "superadmin" && (
                    <button className="hop-link text-xs text-destructive" onClick={() => remove(u)} data-testid={`user-delete-${u.id}`}>
                      <Trash2 size={12} className="inline" /> Удалить
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {resetLink && (
        <div className="grid-cell space-y-4 border-2" data-testid="reset-link-modal">
          <div className="label-mono">RESET-ССЫЛКА · {resetLink.email}</div>
          <p className="text-sm text-foreground/70">
            Скопируйте ссылку и&nbsp;передайте пользователю любым каналом. Срок действия — 1&nbsp;час.
          </p>
          <div className="mono text-xs break-all bg-secondary p-3 border border-black/10">{resetLink.url}</div>
          <div className="flex gap-3">
            <button onClick={copyResetLink} className="btn-primary" data-testid="reset-link-copy">
              {copied ? <><Check size={14} /> Скопировано</> : <><Copy size={14} /> Копировать ссылку</>}
            </button>
            <button onClick={() => setResetLink(null)} className="btn-secondary">Закрыть</button>
          </div>
          <p className="text-xs text-foreground/50">Истекает: {resetLink.expires_at}</p>
        </div>
      )}

      {creating && (
        <div className="grid-cell space-y-4 max-w-xl">
          <div className="label-mono">НОВЫЙ АДМИНИСТРАТОР</div>
          <input className="field-input" placeholder="E-mail" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email-input" />
          <input className="field-input" placeholder="Имя" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <input className="field-input" type="password" placeholder="Временный пароль (мин. 8)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password-input" />
          {err && <p className="text-destructive label-mono">{err}</p>}
          <div className="flex gap-3">
            <button className="btn-secondary" onClick={() => setCreating(false)}>Отмена</button>
            <button className="btn-primary" onClick={create} data-testid="user-create-btn">Создать</button>
          </div>
        </div>
      )}
    </div>
  );
}
